import { useEffect, useState } from "react";
import { X, Save, Loader2, Mic, MicOff, RotateCcw, Film, Camera as CameraIcon } from "lucide-react";
import { toast } from "sonner";
import { useWhisperRecorder } from "@/lib/useWhisperRecorder";
import { runOrQueueMedia } from "@/lib/offlineQueue";

/**
 * PhotoNoteModal
 * --------------
 * Drawer that opens after a photo has been picked by the camera/file input.
 * Lets the sales rep dictate a short voice note (transcribed via Whisper)
 * which is stored as the photo's description on upload.
 *
 * Props:
 *   open         – boolean, controls visibility
 *   file         – the picked File object (preview will be created via URL.createObjectURL)
 *   projectId    – project to attach the photo to
 *   category     – photo category (e.g. "Wohnraum – Heizkörper")
 *   defaultLabel – default text suggestion (used as initial description)
 *   onClose()    – called when user cancels
 *   onUploaded() – called after successful upload
 */
export const PhotoNoteModal = ({
  open,
  file,
  projectId,
  category,
  defaultLabel = "",
  mediaType = "photo",
  onClose,
  onUploaded,
}) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const isVideo = mediaType === "video";

  const recorder = useWhisperRecorder({
    language: "de",
    onTranscript: (text) => {
      setNote((prev) => (prev ? prev + " " : "") + text);
    },
    onError: (msg) => toast.error(msg),
  });

  useEffect(() => {
    if (open && file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setNote("");
      return () => URL.revokeObjectURL(url);
    }
    if (!open) {
      setPreviewUrl(null);
      setNote("");
    }
  }, [open, file]);

  const doUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const desc = note.trim() || defaultLabel || category;
      const r = await runOrQueueMedia({
        projectId,
        file,
        category,
        description: desc,
        label: `${isVideo ? "Video" : "Foto"}: ${category}`,
        mediaType,
      });
      const kind = isVideo ? "Video" : "Foto";
      if (r.offline) {
        toast.success(`Offline – ${kind} wird hochgeladen, sobald online`);
      } else {
        toast.success(note.trim() ? `${kind} + Notiz gespeichert` : `${kind} „${category}" gespeichert`);
      }
      onUploaded?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  };

  const skipNote = () => {
    setNote("");
    doUpload();
  };

  const onMicClick = () => {
    if (recorder.transcribing || uploading) return;
    if (recorder.recording) recorder.stop();
    else recorder.start();
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm"
        onClick={() => !uploading && !recorder.recording && onClose?.()}
        data-testid="photo-note-backdrop"
      />
      {/* Drawer */}
      <div
        data-testid="photo-note-modal"
        className="fixed left-0 right-0 bottom-0 z-[71] bg-[#0E0E12] border-t-2 border-[#00E575]/40 rounded-t-3xl shadow-2xl max-h-[92vh] overflow-y-auto"
      >
        <div className="max-w-3xl mx-auto p-5 pb-8">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="min-w-0">
              <div className="text-[12px] font-mono uppercase tracking-wider text-[#00E575] mb-1 inline-flex items-center gap-1.5">
                {isVideo ? <><Film size={12} /> {category}</> : <><CameraIcon size={12} /> {category}</>}
              </div>
              <h2 className="font-display text-[20px] font-bold text-white leading-tight">
                Sprachnotiz hinzufügen
              </h2>
              <p className="text-[12px] text-zinc-400 mt-1 leading-snug font-medium">
                Optional – drücken Sie das Mikrofon und beschreiben Sie kurz, {isVideo ? "was im Video zu sehen ist." : "was zu sehen ist."}
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={uploading}
              className="h-10 w-10 flex-shrink-0 rounded-xl bg-zinc-800/60 hover:bg-zinc-800 flex items-center justify-center disabled:opacity-50"
              data-testid="photo-note-close"
              aria-label="Schließen"
            >
              <X size={18} />
            </button>
          </div>

          {/* Preview – Image or Video */}
          {previewUrl && !isVideo && (
            <div className="rounded-2xl overflow-hidden bg-[#09090B] border border-zinc-800 mb-4">
              <div className="aspect-[4/3] max-h-[40vh] flex items-center justify-center bg-zinc-950">
                <img
                  src={previewUrl}
                  alt="Vorschau"
                  className="w-full h-full object-contain"
                  data-testid="photo-note-preview"
                />
              </div>
            </div>
          )}
          {previewUrl && isVideo && (
            <div className="rounded-2xl overflow-hidden bg-[#09090B] border border-zinc-800 mb-4">
              <video
                src={previewUrl}
                controls
                playsInline
                className="w-full max-h-[40vh] object-contain bg-zinc-950"
                data-testid="video-note-preview"
              />
              {file && (
                <div className="px-3 py-2 text-[12px] text-zinc-400 font-mono border-t border-zinc-800 flex items-center gap-2">
                  <Film size={12} className="text-[#00E575]" />
                  {(file.size / (1024 * 1024)).toFixed(1)} MB · {file.type || "video"}
                </div>
              )}
            </div>
          )}

          {/* Note textarea + mic */}
          <div className="relative mb-3">
            <textarea
              data-testid="photo-note-textarea"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Notiz eintippen oder per Mikrofon diktieren…"
              disabled={recorder.transcribing}
              className="w-full bg-[#09090B] border border-zinc-700 rounded-xl px-4 py-3 pr-16 text-[15px] text-zinc-50 focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] transition outline-none resize-none disabled:opacity-70"
            />
            <button
              type="button"
              onClick={onMicClick}
              disabled={recorder.transcribing || uploading}
              data-testid="photo-note-mic-btn"
              className={`absolute right-2 top-2 h-11 w-11 rounded-xl flex items-center justify-center transition active:scale-95 disabled:opacity-50 ${
                recorder.recording
                  ? "bg-red-500 text-white shadow-[0_0_15px_rgba(239,68,68,0.6)] animate-pulse"
                  : "bg-[#00E575]/15 text-[#00E575] border border-[#00E575]/40 hover:bg-[#00E575]/25"
              }`}
              title={
                recorder.transcribing
                  ? "Transkribiere…"
                  : recorder.recording
                  ? "Aufnahme beenden"
                  : "Diktieren"
              }
              aria-label={recorder.recording ? "Stop" : "Diktieren"}
            >
              {recorder.transcribing ? (
                <Loader2 size={18} className="animate-spin" />
              ) : recorder.recording ? (
                <MicOff size={18} />
              ) : (
                <Mic size={18} />
              )}
            </button>
            {/* Status indicators */}
            {(recorder.recording || recorder.transcribing) && (
              <div className="mt-2 text-[12px] font-mono inline-flex items-center gap-1.5">
                {recorder.recording ? (
                  <>
                    <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                    <span className="text-red-400">REC · sprechen…</span>
                  </>
                ) : (
                  <>
                    <Loader2 size={12} className="animate-spin text-[#00E575]" />
                    <span className="text-[#00E575]">Transkribiere…</span>
                  </>
                )}
              </div>
            )}
            {note && !recorder.recording && !recorder.transcribing && (
              <button
                type="button"
                onClick={() => setNote("")}
                className="mt-2 inline-flex items-center gap-1 text-[12px] text-zinc-400 hover:text-zinc-200 font-medium"
                data-testid="photo-note-clear"
              >
                <RotateCcw size={12} /> Notiz löschen
              </button>
            )}
          </div>

          {/* Actions */}
          <div className="grid grid-cols-2 gap-2 mt-5">
            <button
              data-testid="photo-note-skip-btn"
              onClick={skipNote}
              disabled={uploading || recorder.recording || recorder.transcribing}
              className="h-13 py-3.5 px-4 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold text-[14px] transition active:scale-[0.97] disabled:opacity-50"
            >
              Ohne Notiz speichern
            </button>
            <button
              data-testid="photo-note-save-btn"
              onClick={doUpload}
              disabled={uploading || recorder.recording || recorder.transcribing}
              className="h-13 py-3.5 px-4 rounded-xl bg-[#00E575] text-[#09090B] font-bold text-[14px] hover:bg-[#00C964] glow-green transition active:scale-[0.97] disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
            >
              {uploading ? <Loader2 size={18} className="animate-spin" /> : <><Save size={16} /> Speichern</>}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
